import React, { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { useSettings } from "../../context/SettingsContext";
import { useAuth } from "../../context/AuthContext";

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [zones, setZones] = useState([]);
  const [saving, setSaving] = useState(false);
  const { refresh } = useSettings();
  const { check } = useAuth();
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [purgeText, setPurgeText] = useState("");

  const purgeTestData = async () => {
    if (purgeText !== "PURGE") return toast.error("Type PURGE to confirm");
    try {
      const { data } = await api.post("/admin/purge-test-data", { confirm: "PURGE" });
      toast.success(`Removed ${data.orders_deleted} orders and ${data.enquiries_deleted} enquiries`);
      setPurgeText("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => setS(data));
    api.get("/admin/zones").then(({ data }) => setZones(data));
  }, []);

  if (!s) return <Loader2 className="animate-spin text-sapore-purple" />;

  const up = (k, v) => setS((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...s, min_order_value: Math.round((parseFloat(s.min_order_value) || 0)) };
      await api.put("/admin/settings", payload);
      refresh();
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const addZone = async () => {
    const { data } = await api.post("/admin/zones", { name: `Zone ${zones.length + 1}`, fee: 200000 });
    setZones((prev) => [...prev, data]);
    refresh();
  };
  const saveZone = async (z, changes) => {
    const { data } = await api.put(`/admin/zones/${z.id}`, changes);
    setZones((prev) => prev.map((x) => (x.id === z.id ? data : x)));
    refresh();
  };
  const delZone = async (z) => { await api.delete(`/admin/zones/${z.id}`); setZones((prev) => prev.filter((x) => x.id !== z.id)); refresh(); };

  const changePassword = async () => {
    if (pw.new_password.length < 8) return toast.error("New password must be at least 8 characters");
    try {
      await api.post("/auth/change-password", pw);
      setPw({ current_password: "", new_password: "" });
      check();
      toast.success("Password changed");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const Section = ({ title, children }) => (
    <div className="bg-white rounded-2xl border border-sapore-border p-6">
      <h2 className="font-serif text-xl text-sapore-purple mb-4">{title}</h2>
      {children}
    </div>
  );
  const Row = ({ label, children }) => (
    <div className="mb-4"><label className="block text-sm font-medium text-sapore-ink mb-1.5">{label}</label>{children}</div>
  );
  const inputCls = "w-full rounded-xl border border-sapore-border p-3 text-sm focus:outline-none focus:ring-2 focus:ring-sapore-purple/30";

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-3xl font-bold text-sapore-purple">Site Settings</h1>
        <button data-testid="save-settings-button" onClick={save} disabled={saving} className="px-5 py-2.5 rounded-full bg-sapore-gold text-sapore-ink font-semibold hover:bg-sapore-goldHover flex items-center gap-2 disabled:opacity-50">
          {saving && <Loader2 size={16} className="animate-spin" />} Save changes
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <Section title="Ordering">
          <label className="flex items-center justify-between mb-4">
            <span className="text-sm text-sapore-ink">Accepting orders</span>
            <input data-testid="toggle-accepting-orders" type="checkbox" checked={s.accepting_orders} onChange={(e) => up("accepting_orders", e.target.checked)} className="w-5 h-5 accent-sapore-purple" />
          </label>
          <Row label="Closed message"><textarea value={s.closed_message} onChange={(e) => up("closed_message", e.target.value)} rows={2} className={inputCls} /></Row>
          <Row label="Minimum order value (₦)"><input data-testid="min-order-input" type="number" value={Math.round((s.min_order_value || 0) / 100)} onChange={(e) => up("min_order_value", (parseInt(e.target.value) || 0) * 100)} className={inputCls} /></Row>
        </Section>

        <Section title="Announcement banner">
          <label className="flex items-center justify-between mb-4">
            <span className="text-sm text-sapore-ink">Banner active</span>
            <input data-testid="toggle-banner" type="checkbox" checked={s.banner_active} onChange={(e) => up("banner_active", e.target.checked)} className="w-5 h-5 accent-sapore-purple" />
          </label>
          <Row label="Banner text"><input data-testid="banner-text-input" value={s.banner_text} onChange={(e) => up("banner_text", e.target.value)} className={inputCls} /></Row>
        </Section>

        <Section title="Contact details">
          <Row label="Phone"><input value={s.phone} onChange={(e) => up("phone", e.target.value)} className={inputCls} /></Row>
          <Row label="WhatsApp number (e.g. 2348094706281)"><input value={s.whatsapp} onChange={(e) => up("whatsapp", e.target.value)} className={inputCls} /></Row>
          <Row label="Email"><input value={s.email} onChange={(e) => up("email", e.target.value)} className={inputCls} /></Row>
          <Row label="Instagram URL"><input value={s.instagram} onChange={(e) => up("instagram", e.target.value)} className={inputCls} /></Row>
          <Row label="Business hours"><input value={s.business_hours} onChange={(e) => up("business_hours", e.target.value)} className={inputCls} /></Row>
          <Row label="Google Analytics ID"><input value={s.ga_id} onChange={(e) => up("ga_id", e.target.value)} className={inputCls} /></Row>
        </Section>

        <Section title="Delivery zones">
          {zones.map((z) => (
            <div key={z.id} className="flex gap-2 mb-2" data-testid={`zone-row-${z.id}`}>
              <input defaultValue={z.name} onBlur={(e) => saveZone(z, { name: e.target.value })} className="flex-1 rounded-lg border border-sapore-border p-2 text-sm" />
              <input type="number" defaultValue={z.fee} onBlur={(e) => saveZone(z, { fee: parseInt(e.target.value) || 0 })} className="w-32 rounded-lg border border-sapore-border p-2 text-sm" title="Fee in kobo" />
              <button onClick={() => delZone(z)} className="text-red-500"><Trash2 size={16} /></button>
            </div>
          ))}
          <button data-testid="add-zone-button" onClick={addZone} className="text-sm text-sapore-purple flex items-center gap-1 mt-2"><Plus size={14} /> Add zone</button>
          <p className="text-xs text-sapore-muted mt-2">Fees are stored in kobo (₦2,000 = 200000).</p>
        </Section>

        <Section title="Policy pages">
          <Row label="Terms & Conditions"><textarea value={s.terms} onChange={(e) => up("terms", e.target.value)} rows={3} className={inputCls} /></Row>
          <Row label="Privacy Policy"><textarea value={s.privacy} onChange={(e) => up("privacy", e.target.value)} rows={3} className={inputCls} /></Row>
          <Row label="Refund & Cancellation"><textarea value={s.refund} onChange={(e) => up("refund", e.target.value)} rows={3} className={inputCls} /></Row>
        </Section>

        <Section title="Test data">
          <p className="text-sm text-sapore-inkSoft mb-3">Permanently delete every order and enquiry flagged as test data. This cannot be undone — type <strong>PURGE</strong> to confirm.</p>
          <div className="flex gap-2 max-w-sm">
            <input value={purgeText} onChange={(e) => setPurgeText(e.target.value)} placeholder="Type PURGE" className={inputCls} data-testid="purge-input" />
            <button data-testid="purge-button" onClick={purgeTestData} disabled={purgeText !== "PURGE"} className="px-5 py-2.5 rounded-full bg-red-600 text-white font-medium whitespace-nowrap disabled:opacity-50">Purge test data</button>
          </div>
        </Section>

        <Section title="Change your password">
          <Row label="Current password"><input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className={inputCls} data-testid="current-password" /></Row>
          <Row label="New password"><input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className={inputCls} data-testid="new-password" /></Row>
          <button data-testid="change-password-button" onClick={changePassword} className="px-5 py-2.5 rounded-full bg-sapore-purple text-sapore-cream font-medium flex items-center gap-2"><KeyRound size={16} /> Update password</button>
        </Section>
      </div>
    </div>
  );
}
